import React, { useState, useCallback, useMemo } from 'react'
import MoleculeViewer from '../../NGL/NGLMolViz'
import { FieldGroupWrapper, GridWrapper, DownloadPdbBox } from '../../../style/StyledElement'
import SpinnerButton from '../../../components/common/SpinnerButton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { useTheme, TextField, Alert, Snackbar, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { API_BASE_URL } from '../../../configs/Constants'

// Download state constants
const DOWNLOAD_STATES = {
  IDLE: 'IDLE',
  DOWNLOADING: 'DOWNLOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

// Styled components for professional appearance
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2),
}))

const InputSection = styled(Card)(({ theme }) => ({
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const ViewerSection = styled(Card)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiFilledInput-root': {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: '#6B7280',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}))

export const ProteinStructPrediction = () => {
  const theme = useTheme()
  
  // State management
  const [uniprotId, setUniprotId] = useState(
    localStorage.getItem('proteinPredSmile') || ''
  )
  const [inputValue, setInputValue] = useState(uniprotId)
  const [downloadState, setDownloadState] = useState(DOWNLOAD_STATES.IDLE)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  })

  // Validate Uniprot ID format
  const validateUniprotId = useCallback((id) => {
    const trimmedId = id.trim()
    
    if (!trimmedId) {
      setError('Uniprot ID is required')
      return false
    }

    // Uniprot ID validation: typically 6-10 characters, alphanumeric
    const uniprotPattern = /^[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/i
    if (!uniprotPattern.test(trimmedId)) {
      // More lenient validation for edge cases
      const basicPattern = /^[A-Z0-9]{6,10}$/i
      if (!basicPattern.test(trimmedId)) {
        setError('Invalid Uniprot ID format. Expected format: P12345 or Q9Y6R4')
        return false
      }
    }

    return true
  }, [])

  // Handle input changes
  const handleInputChange = useCallback((event) => {
    const value = event.target.value
    setInputValue(value)
    setError(null)
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(() => {
    const trimmedInput = inputValue.trim()
    
    if (!validateUniprotId(trimmedInput)) {
      return
    }

    const upperCaseId = trimmedInput.toUpperCase()
    setUniprotId(upperCaseId)
    localStorage.setItem('proteinPredSmile', upperCaseId)
    
    setNotification({
      open: true,
      message: 'Protein structure loaded successfully',
      severity: 'success'
    })
  }, [inputValue, validateUniprotId])

  // Handle Enter key press
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }, [handleSubmit])

  // Clear input and reset state
  const handleClear = useCallback(() => {
    setInputValue('')
    setUniprotId('')
    setError(null)
    localStorage.removeItem('proteinPredSmile')
    
    setNotification({
      open: true,
      message: 'Cleared protein data',
      severity: 'info'
    })
  }, [])

  // Download PDB file with improved error handling
  const getPDBFile = useCallback(async () => {
    if (!uniprotId) {
      setNotification({
        open: true,
        message: 'No Uniprot ID available for download',
        severity: 'warning'
      })
      return
    }

    setDownloadState(DOWNLOAD_STATES.DOWNLOADING)

    try {
      const url = `${API_BASE_URL}/data/omega/fasta/${uniprotId}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/octet-stream'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty')
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${uniprotId}.pdb`
      link.style.display = 'none'
      
      // Append to document, click, and cleanup
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      setDownloadState(DOWNLOAD_STATES.SUCCESS)
      setNotification({
        open: true,
        message: `PDB file ${uniprotId}.pdb downloaded successfully`,
        severity: 'success'
      })

    } catch (err) {
      console.error('Download failed:', err)
      setDownloadState(DOWNLOAD_STATES.ERROR)
      
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to download PDB file'
        
      setNotification({
        open: true,
        message: `Download failed: ${errorMessage}`,
        severity: 'error'
      })
    }
  }, [uniprotId])

  // Close notification
  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }))
  }, [])

  // Memoized derived states
  const isSubmitDisabled = useMemo(() => !inputValue.trim(), [inputValue])
  const isDownloading = useMemo(() => downloadState === DOWNLOAD_STATES.DOWNLOADING, [downloadState])
  const hasValidProtein = useMemo(() => Boolean(uniprotId), [uniprotId])

  // Responsive styles
  const containerStyles = useMemo(() => ({
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 2
    },
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%'
    }
  }), [theme])

  return (
    <>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Protein Structure Prediction
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Visualize and analyze 3D protein structures using UniProt database
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="3D Visualization" size="small" />
              <FeatureChip label="PDB Export" size="small" />
              <FeatureChip label="UniProt Integration" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🧬
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Input Section */}
        <InputSection elevation={0}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              UniProt ID Input
            </Typography>
            <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />
            
            <FieldGroupWrapper>
              <Box sx={containerStyles}>
                <Box sx={{ flex: 1, minWidth: '300px' }}>
                  <StyledTextField
                    fullWidth
                    variant="filled"
                    label="UNIPROT ID"
                    placeholder="Enter Uniprot ID (e.g., P12345)"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    error={Boolean(error)}
                    helperText={error || 'Enter a valid Uniprot ID to load protein structure'}
                    autoComplete="off"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, md: 0 } }}>
                  <SpinnerButton
                    label="Load Structure"
                    className="btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                    sx={{
                      background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                      },
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  />
                  <SpinnerButton
                    label="Clear"
                    variant="outlined"
                    onClick={handleClear}
                    sx={{ 
                      color: '#6B7280',
                      borderColor: '#D1D5DB',
                      '&:hover': {
                        borderColor: '#6B7280',
                        backgroundColor: '#F9FAFB'
                      }
                    }}
                  />
                </Box>
              </Box>
            </FieldGroupWrapper>
          </CardContent>
        </InputSection>

        {/* Structure Viewer Section */}
        {hasValidProtein && (
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              3D Structure Visualization
            </Typography>
            <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />
            
            <ViewerSection elevation={0}>
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 0,
                  backgroundColor: '#EFF6FF',
                  borderColor: '#BFDBFE',
                  color: '#1E40AF'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography>
                    Displaying protein structure for UniProt ID: <strong>{uniprotId}</strong>
                  </Typography>
                </Box>
              </Alert>
              
              <GridWrapper>
                <MoleculeViewer uniprotId={uniprotId} />
              </GridWrapper>
            </ViewerSection>
          </Box>
        )}
      </ContentContainer>

      {/* Download section */}
      {hasValidProtein && (
        <DownloadPdbBox>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Export Structure
            </Typography>
            <SpinnerButton
              label={isDownloading ? 'Downloading...' : 'Download PDB File'}
              className="btn-primary"
              onClick={getPDBFile}
              loading={isDownloading}
              disabled={isDownloading}
              sx={{
                width: { xs: '100%', sm: 300 },
                background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                }
              }}
            />
          </Box>
        </DownloadPdbBox>
      )}

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  )
}